import { FC, useEffect, useMemo } from "react";

import { PageLink } from "./PageLink";
import { useFetchAndSelectMyAssessments } from "./assessment-data";

import { getPageType, tableDateFormat } from "components/materials/page/helpers";
import { displayOutcomeName } from "components/outcomes/helpers";
import { ActivityType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { Tooltip } from "mds/components/Tooltip";
import { AssessPointsMarkerIcon, CommentIcon, ScoreIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  activitySelectors,
  pageOutcomeSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectFullSectionOutcomes,
  selectPagesWithAssessmentsForUserId,
  userSelectors,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { averageAndRound } from "utils/math";

type SectionAssessmentStudentViewProps = {
  userId: string | null;
};

export const SectionAssessmentStudentView: FC<SectionAssessmentStudentViewProps> = ({ userId }) => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const sectionOutcomes = useAppSelector(selectFullSectionOutcomes);
  // TODO: Should we bundle these in the selectors somehow?
  const pageOutcomes = useAppSelector(pageOutcomeSelectors.selectAll);
  const activities = useListSelector(activitySelectors.selectAll);
  const studentUser = useAppSelector((s) => userSelectors.selectById(s, userId));
  const isViewingOwnAssessments = currentUserId === userId;

  // This includes page groups and submissions
  useFetchAndSelectMyAssessments();

  useEffect(() => {
    if (sectionId) {
      storeApi.page_outcomes.list({ section_id: sectionId });
    }
  }, [sectionId]);

  const activitiesById = useMemo(
    () =>
      activities.reduce(
        (acc, activity) => ({
          ...acc,
          [activity.id]: activity,
        }),
        {} as Record<string, ActivityType>,
      ),
    [activities],
  );

  const pagesWithAssessments = useAppSelector((s) =>
    selectPagesWithAssessmentsForUserId(s, userId, sectionId),
  );
  const allAssessments = pagesWithAssessments.map((p) => p.assessments).flat();
  const allOutcomeAssessments = allAssessments.map((a) => a.outcome_assessments || []).flat();

  if (pagesWithAssessments.length === 0) {
    return (
      <EmptyCard
        className="mb-4"
        description={t(
          isViewingOwnAssessments
            ? "assessment_dashboard.all.empty_description"
            : "assessment_dashboard.all.empty_description_instructor",
          {
            studentName: studentUser?.first_name || t("assessment_dashboard.all.unknown_student"),
          },
        )}
        icon={<AssessPointsMarkerIcon className="text-blue" />}
        title={t(
          isViewingOwnAssessments
            ? "assessment_dashboard.all.empty_title"
            : "assessment_dashboard.all.empty_title_instructor",
        )}
      />
    );
  }

  return (
    <main className="assessment-dashboard overflow-hidden">
      <ScrollableTableContainer
        className="w-full overflow-hidden"
        title={t("assessment_dashboard.all.title")}
      >
        <Table>
          {/* Headers: Page / Page Type / Points / Outcomes / Submitted At */}
          <THead>
            <TR className="border-black-tint-70">
              <TH className="pl-4">{t("assessment_dashboard.all.page_topic_header_title")}</TH>

              <TH>{t("assessment_dashboard.all.page_header_title")}</TH>

              <TH>
                <Tooltip text={t("assessment_dashboard.all.page_type_header_tooltip")}>
                  {t("assessment_dashboard.all.page_type_header_title")}
                </Tooltip>
              </TH>

              <TH>{t("assessment_dashboard.student.points_header_title")}</TH>

              <TH>{t("assessment_dashboard.all.outcomes_header_title")}</TH>

              <TH>{t("assessment_dashboard.student.submitted_header_title")}</TH>
            </TR>
          </THead>

          <tbody>
            {pagesWithAssessments.map((page) => {
              // TODO: The selector should allow for non-final submissions so that we can display comments here
              // There should never be more than one assessment for a page and user, but just
              // in case something fishy happens, we handle it gracefully here.
              const officialAssessment = page.assessments[0];
              const points = officialAssessment?.points;
              const submittedDate =
                officialAssessment?.submission &&
                tableDateFormat(officialAssessment?.submission?.created_at);

              const assessmentsWithComments = page.nonOfficialAssessments
                .concat(page.assessments)
                .filter((a) => a.comment?.trim());
              const pageSpecificOutcomes = pageOutcomes.filter((po) => po.page_id === page.id);

              return (
                <TR key={page.id}>
                  <TD className="max-w-52 truncate py-2.5 pl-[17px]" title={page.topic?.title}>
                    {page.topic?.title || "–"}
                  </TD>

                  <TD title={page.title}>
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 max-w-40 truncate">
                        <PageLink
                          className="w-full truncate text-blue-shade-20 underline"
                          page={page}
                        />
                      </div>
                      {assessmentsWithComments.length > 0 &&
                        assessmentsWithComments.map((assessment) => (
                          <Tooltip
                            key={assessment.id}
                            text={
                              <div className="body-xs text-black-tint-40">{assessment.comment}</div>
                            }
                          >
                            <CommentIcon />
                          </Tooltip>
                        ))}
                    </div>
                  </TD>

                  <TD>{getPageType(page, activitiesById)}</TD>

                  <TD>{typeof points === "number" ? `${points}/${page.max_points}` : "-"}</TD>

                  <TD>
                    <Tooltip
                      text={pageSpecificOutcomes
                        .flatMap((po) => {
                          const sectionOutcome = sectionOutcomes.find(
                            (co) => co.id === po.section_outcome_id,
                          );
                          return sectionOutcome ? [displayOutcomeName(sectionOutcome.outcome)] : [];
                        })
                        .join(", ")}
                    >
                      {pageSpecificOutcomes.length || "0"}
                    </Tooltip>
                  </TD>

                  <TD>{submittedDate || "-"}</TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      </ScrollableTableContainer>

      <ScrollableTableContainer title={t("assessment_dashboard.student_outcome_index_title")}>
        <Table>
          {/* Headers: Page / Page Type/ Outcome A / Outcome B / Outcome C */}
          {/* One row per student, showing each student's aggregate outcome scores */}
          <THead>
            <TR>
              <TH className="w-4/12 pl-4">{t("assessment_dashboard.all.page_header_title")}</TH>

              <TH className="w-2/12">
                <Tooltip text={t("assessment_dashboard.all.page_type_header_tooltip")}>
                  {t("assessment_dashboard.all.page_type_header_title")}
                </Tooltip>
              </TH>

              {sectionOutcomes.map((sectionOutcome) => (
                <TH
                  key={sectionOutcome.id}
                  verticalDivClassName="max-h-[180px]"
                  verticalDivTitle={displayOutcomeName(sectionOutcome.outcome)}
                  isVertical
                >
                  {displayOutcomeName(sectionOutcome.outcome)}
                </TH>
              ))}
            </TR>
          </THead>

          <tbody>
            {pagesWithAssessments.map((page) => (
              <TR key={page.id}>
                <TD className="min-w-0 max-w-24 truncate py-2.5 pl-[17px]">
                  <PageLink className="truncate text-blue-shade-20 underline" page={page} />
                </TD>

                <TD>{getPageType(page, activitiesById)}</TD>

                {sectionOutcomes.map((sectionOutcome) => {
                  const userOutcomeAssessmentsForThisOutcome = allOutcomeAssessments.filter((oa) =>
                    pageOutcomes.some(
                      (po) =>
                        po.page_id === page.id &&
                        po.section_outcome_id === sectionOutcome.id &&
                        oa.page_outcome_id === po.id,
                    ),
                  );

                  const filteredUserOutcomeAssessmentsForThisOutcome =
                    userOutcomeAssessmentsForThisOutcome.filter(
                      (oa) => oa.score !== null && oa.score !== undefined,
                    );
                  const outcomeScore = filteredUserOutcomeAssessmentsForThisOutcome.length
                    ? averageAndRound(
                        filteredUserOutcomeAssessmentsForThisOutcome.map((oa) => oa.score),
                      )
                    : NaN;

                  return (
                    <TD key={sectionOutcome.id}>
                      <ScoreIcon value={outcomeScore} />
                    </TD>
                  );
                })}
              </TR>
            ))}
          </tbody>
        </Table>
      </ScrollableTableContainer>
    </main>
  );
};
